import { SiteUrls } from '@tableau/shared-urls';

import { SpacesSiteUrls } from '../../app/spaces/SpacesSiteUrls';
import { hideLaunchEditOnMobile } from '../../app/utils/MobileHelper';
import { Messages } from '../../codegen/Localize';
import { getSessionManagementServiceInstance } from '../../sessionManagement/SessionManagementService';
import { FeatureFlags } from '../../ts/FeatureFlags';
import { SiteRoles } from '../../ts/ServerApi/types/ISiteRole';
import { WelcomeChannelStateProps } from '../components/WelcomeChannel';
import { WelcomeChannelLinks } from './WelcomeChannelLinks';

export namespace WelcomeChannelProps {
  /**
   * Helper function
   * This function needs to be called inside a react component
   */
  export const getPropsByRole = (role: string, canWebAuthor: boolean): WelcomeChannelStateProps => {
    const siteUrls = new SpacesSiteUrls({
      siteUrlName: getSessionManagementServiceInstance().sessionSiteUrlName(),
    });

    const welcomeChannelPropsByRole = new WelcomeChannelPropsByRole(
      siteUrls,
      canWebAuthor);
    return welcomeChannelPropsByRole.getProps(role);
  };
}

class WelcomeChannelPropsByRole {
  private links: WelcomeChannelLinks;
  private canWebAuthor: boolean;

  constructor(siteUrls: SiteUrls, canWebAuthor: boolean) {
    this.links = new WelcomeChannelLinks(siteUrls);
    this.canWebAuthor = canWebAuthor;
  }

  getProps(role: string): WelcomeChannelStateProps {
    switch (role) {

      case SiteRoles.ServerAdministrator:
      case SiteRoles.SiteAdministratorCreator:
      case SiteRoles.SupportUser:
        return {
          header: Messages.home_welcomeChannel_HeaderAdmin(),
          subHeader: Messages.home_welcomeChannel_SubheaderAdmin(),
          primaryButtonLink: this.createWorkbookIfWebAuthoringEnabled(),
          showCreateMenu: true,
          bottomLinks: [
            ...FeatureFlags.uploadWorkbookFromWebEnabled ? [this.links.uploadWorkbook()] : [],
            this.links.manageProjects(),
            this.links.manageUsers(),
            this.links.downloadDesktop(),
            this.links.downloadPrep(),
          ],
        };

      case SiteRoles.SiteAdministratorExplorer:
        return {
          header: Messages.home_welcomeChannel_HeaderAdmin(),
          subHeader: Messages.home_welcomeChannel_SubheaderAdmin(),
          primaryButtonLink: this.links.manageProjects(),
          showCreateMenu: false,
          bottomLinks: [
            ...FeatureFlags.uploadWorkbookFromWebEnabled ? [this.links.uploadWorkbook()] : [],
            this.links.manageUsers(),
            this.createWorkbookIfWebAuthoringEnabled(),
          ],
        };

      case SiteRoles.Creator:
        return {
          header: Messages.home_welcomeChannel_HeaderNonAdmin(),
          subHeader: Messages.home_welcomeChannel_SubheaderNonAdmin(),
          primaryButtonLink: this.createWorkbookIfWebAuthoringEnabled(),
          showCreateMenu: true,
          bottomLinks: [
            ...FeatureFlags.uploadWorkbookFromWebEnabled ? [this.links.uploadWorkbook()] : [],
            this.links.downloadDesktop(),
            this.links.downloadPrep(),
          ],
        };

      case SiteRoles.Explorer:
        return {
          header: Messages.home_welcomeChannel_HeaderNonAdmin(),
          subHeader: Messages.home_welcomeChannel_SubheaderNonAdmin(),
          primaryButtonLink: this.links.startExploring(),
          showCreateMenu: false,
          bottomLinks: [],
        };

      case SiteRoles.ExplorerCanPublish:
        return {
          header: Messages.home_welcomeChannel_HeaderNonAdmin(),
          subHeader: Messages.home_welcomeChannel_SubheaderNonAdmin(),
          primaryButtonLink: this.links.startExploring(),
          showCreateMenu: false,
          bottomLinks: this.canWebAuthor ? [
            ...!hideLaunchEditOnMobile() ? [this.links.createWorkbook()] : [],
            ...FeatureFlags.uploadWorkbookFromWebEnabled ? [this.links.uploadWorkbook()] : []
          ] : [],
        };

      case SiteRoles.Viewer:
      case SiteRoles.Guest:
        return {
          header: Messages.home_welcomeChannel_HeaderNonAdmin(),
          subHeader: Messages.home_welcomeChannel_SubheaderNonAdmin(),
          primaryButtonLink: this.links.startExploring(),
          showCreateMenu: false,
          bottomLinks: [],
        };

      default:
        return {
          header: Messages.home_welcomeChannel_HeaderNonAdmin(),
          subHeader: Messages.home_welcomeChannel_SubheaderNonAdmin(),
          primaryButtonLink: this.links.startExploring(),
          showCreateMenu: false,
          bottomLinks: [],
        };
    }
  }

  // TFS 960183: Create Workbook needs to respect web authoring enabled/disabled setting
  createWorkbookIfWebAuthoringEnabled() {
    return this.canWebAuthor && !hideLaunchEditOnMobile() ? this.links.createWorkbook() : this.links.startExploring();
  }
}
